import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Search, Users, Mail, Phone, MapPin, ShoppingBag,
  DollarSign, Calendar, ChevronRight, UserPlus, LogIn, KeyRound, Star, X,
  MoreVertical, Pencil, Trash2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../../components/ui/alert-dialog';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../context/AuthContext';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCustomers = () => {
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [creatingTestCustomer, setCreatingTestCustomer] = useState(false);
  const [impersonatingCustomerId, setImpersonatingCustomerId] = useState('');
  const [resetPwOpen, setResetPwOpen] = useState(false);
  const [resetPwUser, setResetPwUser] = useState(null);
  const [resetPwValue, setResetPwValue] = useState('');

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/store/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
    setLoading(false);
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleCreateTestCustomer = async () => {
    setCreatingTestCustomer(true);
    try {
      const response = await axios.post(`${API}/admin/customers/create-test`, {}, {
        headers: getAuthHeaders(),
      });

      toast({
        title: response.data?.created ? 'Test customer created' : 'Test customer already exists',
        description: `${response.data?.email} / ${response.data?.password}`,
      });

      await fetchCustomers();
    } catch (error) {
      toast({
        title: 'Failed to create test customer',
        description: error.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setCreatingTestCustomer(false);
    }
  };

  const handleImpersonateCustomer = async (customer) => {
    setImpersonatingCustomerId(customer.id);
    try {
      const response = await axios.post(
        `${API}/admin/customers/${customer.id}/impersonate`,
        {},
        { headers: getAuthHeaders() }
      );

      const { access_token, user } = response.data;
      const result = await startImpersonation(access_token, user);
      if (!result.success) {
        throw new Error(result.error || 'Could not start impersonation');
      }

      toast({
        title: 'Impersonation active',
        description: `Now signed in as ${user?.email || customer.email}`,
      });
      window.location.assign('/account');
    } catch (error) {
      toast({
        title: 'Impersonation failed',
        description: error.response?.data?.detail || error.message || 'Unable to impersonate this customer.',
        variant: 'destructive',
      });
    } finally {
      setImpersonatingCustomerId('');
    }
  };

  const handleResetCustomerPassword = async () => {
    if (!resetPwUser || !resetPwValue || resetPwValue.length < 6) {
      toast({ title: 'Error', description: 'Password must be at least 6 characters.', variant: 'destructive' });
      return;
    }
    try {
      await axios.put(`${API}/users/reset-password/${resetPwUser.user_id || resetPwUser.id}`, { new_password: resetPwValue });
      toast({ title: 'Password Reset', description: `Password updated for ${resetPwUser.email}` });
      setResetPwOpen(false);
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to reset password.', variant: 'destructive' });
    }
  };

  const handleViewCustomer = (customer) => {
    navigate(`/admin/user-management/customers/${customer.id}`);
  };

  const [editOpen, setEditOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', address: '', city: '', state: '', zip_code: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const openEditModal = (customer) => {
    setEditCustomer(customer);
    setEditForm({
      name: customer.name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      city: customer.city || '',
      state: customer.state || '',
      zip_code: customer.zip_code || '',
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim() || !editForm.email.trim()) {
      toast({ title: 'Missing fields', description: 'Name and email are required.', variant: 'destructive' });
      return;
    }
    setSavingEdit(true);
    try {
      const res = await axios.put(`${API}/store/customers/${editCustomer.id}`, editForm, { headers: getAuthHeaders() });
      setCustomers(prev => prev.map(c => (c.id === editCustomer.id ? { ...c, ...res.data } : c)));
      toast({ title: 'Customer updated', description: `${editForm.name}'s details have been saved.` });
      setEditOpen(false);
    } catch (error) {
      toast({
        title: 'Failed to update customer',
        description: error.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSavingEdit(false);
    }
  };

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteCustomer, setDeleteCustomer] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const openDeleteConfirm = (customer) => {
    setDeleteCustomer(customer);
    setDeleteOpen(true);
  };

  const handleDeleteCustomer = async () => {
    if (!deleteCustomer) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/store/customers/${deleteCustomer.id}`, { headers: getAuthHeaders() });
      setCustomers(prev => prev.filter(c => c.id !== deleteCustomer.id));
      toast({ title: 'Customer deleted', description: `${deleteCustomer.name} has been removed.` });
      setDeleteOpen(false);
    } catch (error) {
      toast({
        title: 'Failed to delete customer',
        description: error.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleVip = async (customer) => {
    const userId = customer.user_id || customer.id;
    const newState = !customer.is_vip;
    // Optimistic update
    setCustomers(prev => prev.map(c =>
      (c.id === customer.id || c.user_id === userId) ? { ...c, is_vip: newState } : c
    ));
    try {
      await axios.patch(`${API}/admin/users/${userId}/vip`, {}, { headers: getAuthHeaders() });
      toast({
        title: newState ? '⭐ VIP Activated' : 'VIP Removed',
        description: `${customer.email} is ${newState ? 'now a Featured VIP — unlimited site registrations on all licenses.' : 'no longer VIP.'}`,
      });
    } catch (error) {
      // Revert on failure
      setCustomers(prev => prev.map(c =>
        (c.id === customer.id || c.user_id === userId) ? { ...c, is_vip: !newState } : c
      ));
      toast({
        title: 'Failed to update VIP status',
        description: error.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', password: '', phone: '', company: '', notes: '' });
  const [addingCustomer, setAddingCustomer] = useState(false);

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    if (!addForm.name.trim() || !addForm.email.trim() || !addForm.password.trim()) {
      toast({ title: 'Missing fields', description: 'Name, email and password are required.', variant: 'destructive' });
      return;
    }
    setAddingCustomer(true);
    try {
      await axios.post(`${API}/admin/customers/create`, addForm, { headers: getAuthHeaders() });
      toast({ title: 'Customer created', description: `${addForm.name} (${addForm.email}) has been added.` });
      setShowAddModal(false);
      setAddForm({ name: '', email: '', password: '', phone: '', company: '', notes: '' });
      await fetchCustomers();
    } catch (error) {
      toast({
        title: 'Failed to create customer',
        description: error.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    }
    setAddingCustomer(false);
  };

  const filteredCustomers = customers.filter(customer => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return customer.name.toLowerCase().includes(query) ||
             customer.email.toLowerCase().includes(query);
    }
    return true;
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
        <p className="text-gray-500">View and manage your customer base</p>
      </div>

      <div className="flex gap-2 justify-end">
        <Button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="add-customer-button"
        >
          <UserPlus className="w-4 h-4 mr-2" /> Add New Customer
        </Button>
        <Button
          variant="outline"
          onClick={handleCreateTestCustomer}
          disabled={creatingTestCustomer}
          className="text-gray-500 border-gray-300"
          data-testid="create-test-customer-button"
        >
          {creatingTestCustomer ? 'Creating...' : 'Create Test Account'}
        </Button>
      </div>

      {/* Add New Customer Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-lg">
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-blue-600" /> Add New Customer
          </DialogTitle>
          <form onSubmit={handleAddCustomer} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="new-name">Full Name <span className="text-red-500">*</span></Label>
                <Input
                  id="new-name"
                  value={addForm.name}
                  onChange={e => setAddForm({...addForm, name: e.target.value})}
                  placeholder="John Smith"
                  required
                  data-testid="new-customer-name"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="new-email">Email Address <span className="text-red-500">*</span></Label>
                <Input
                  id="new-email"
                  type="email"
                  value={addForm.email}
                  onChange={e => setAddForm({...addForm, email: e.target.value})}
                  placeholder="john@example.com"
                  required
                  data-testid="new-customer-email"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="new-password">Password <span className="text-red-500">*</span></Label>
                <Input
                  id="new-password"
                  type="password"
                  value={addForm.password}
                  onChange={e => setAddForm({...addForm, password: e.target.value})}
                  placeholder="Minimum 8 characters"
                  required
                  data-testid="new-customer-password"
                />
              </div>
              <div>
                <Label htmlFor="new-phone">Phone</Label>
                <Input
                  id="new-phone"
                  value={addForm.phone}
                  onChange={e => setAddForm({...addForm, phone: e.target.value})}
                  placeholder="(555) 000-0000"
                  data-testid="new-customer-phone"
                />
              </div>
              <div>
                <Label htmlFor="new-company">Company</Label>
                <Input
                  id="new-company"
                  value={addForm.company}
                  onChange={e => setAddForm({...addForm, company: e.target.value})}
                  placeholder="Acme Inc."
                  data-testid="new-customer-company"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="new-notes">Notes</Label>
                <Input
                  id="new-notes"
                  value={addForm.notes}
                  onChange={e => setAddForm({...addForm, notes: e.target.value})}
                  placeholder="Internal notes (optional)"
                  data-testid="new-customer-notes"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={addingCustomer} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-customer-save">
                <UserPlus className="w-4 h-4 mr-2" />
                {addingCustomer ? 'Saving...' : 'Save & Close'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Customers</p>
                <p className="text-2xl font-bold">{customers.length}</p>
              </div>
              <Users className="w-10 h-10 text-[rgb(37, 99, 235)]/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Revenue</p>
                <p className="text-2xl font-bold">
                  ${customers.reduce((sum, c) => sum + (c.total_spent || 0), 0).toLocaleString()}
                </p>
              </div>
              <DollarSign className="w-10 h-10 text-[rgb(37, 99, 235)]/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Orders</p>
                <p className="text-2xl font-bold">
                  {customers.reduce((sum, c) => sum + (c.total_orders || 0), 0)}
                </p>
              </div>
              <ShoppingBag className="w-10 h-10 text-[rgb(37, 99, 235)]/20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search customers by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {/* Customers Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-[rgb(37, 99, 235)] border-t-transparent rounded-full" />
            </div>
          ) : filteredCustomers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Customer</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Contact</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Location</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Orders</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Total Spent</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-500">VIP</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((customer) => (
                    <tr key={customer.id} className={`border-b hover:bg-gray-50 ${customer.is_vip ? 'bg-amber-50/60' : ''}`}>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${customer.is_vip ? 'bg-amber-500' : 'bg-[rgb(37,99,235)]'}`}>
                            {customer.is_vip
                              ? <Star className="w-5 h-5 fill-white" />
                              : customer.name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <span className="font-medium">{customer.name}</span>
                            {customer.is_vip && (
                              <span className="ml-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200">
                                ⭐ VIP
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="space-y-1">
                          <p className="text-sm flex items-center gap-1">
                            <Mail className="w-3 h-3 text-gray-400" /> {customer.email}
                          </p>
                          {customer.phone && (
                            <p className="text-sm text-gray-500 flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {customer.phone}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {customer.city && customer.state ? (
                          <span className="text-sm text-gray-500">
                            {customer.city}, {customer.state}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="outline">{customer.total_orders || 0} orders</Badge>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-semibold text-green-600">
                          ${customer.total_spent?.toFixed(2) || '0.00'}
                        </span>
                      </td>
                      {/* VIP toggle */}
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => handleToggleVip(customer)}
                          title={customer.is_vip ? 'Remove VIP — click to revoke unlimited site access' : 'Grant Featured VIP — unlimited site registrations on all licenses'}
                          data-testid={`vip-toggle-${customer.id}`}
                          className={`w-9 h-9 rounded-full flex items-center justify-center mx-auto transition-all border ${
                            customer.is_vip
                              ? 'bg-amber-400 border-amber-500 text-white shadow-sm shadow-amber-200 hover:bg-amber-500'
                              : 'bg-gray-100 border-gray-200 text-gray-400 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-400'
                          }`}
                        >
                          <Star className={`w-4 h-4 ${customer.is_vip ? 'fill-white' : ''}`} />
                        </button>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleImpersonateCustomer(customer)}
                            disabled={impersonatingCustomerId === customer.id}
                            data-testid={`impersonate-customer-button-${customer.id}`}
                          >
                            <LogIn className="w-4 h-4 mr-1" />
                            {impersonatingCustomerId === customer.id ? 'Impersonating...' : 'Impersonate'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setResetPwUser(customer); setResetPwValue(''); setResetPwOpen(true); }}
                            className="text-amber-500 hover:text-amber-700"
                            data-testid={`reset-password-customer-${customer.id}`}
                          >
                            <KeyRound className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewCustomer(customer)}
                            className="rounded-r-none border-r-0"
                            data-testid={`view-customer-button-${customer.id}`}
                          >
                            View <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="rounded-l-none px-2"
                                data-testid={`customer-actions-menu-${customer.id}`}
                              >
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => openEditModal(customer)}
                                data-testid={`edit-customer-menu-item-${customer.id}`}
                              >
                                <Pencil className="w-4 h-4 mr-2" /> Edit Customer
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => openDeleteConfirm(customer)}
                                className="text-red-600 focus:text-red-600"
                                data-testid={`delete-customer-menu-item-${customer.id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Delete Customer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No customers found</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={resetPwOpen} onOpenChange={setResetPwOpen}>
        <DialogContent className="max-w-sm">
          <DialogTitle>Reset Customer Password</DialogTitle>
          <p className="text-sm text-gray-500">Set a new password for {resetPwUser?.name || resetPwUser?.email}</p>
          <div className="space-y-4 mt-2">
            <div>
              <Label>New Password</Label>
              <Input type="text" value={resetPwValue} onChange={(e) => setResetPwValue(e.target.value)} placeholder="Enter new password" className="mt-1" data-testid="reset-customer-password-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setResetPwOpen(false)}>Cancel</Button>
              <Button onClick={handleResetCustomerPassword} data-testid="reset-customer-password-confirm">Reset Password</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Modal */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <Pencil className="w-5 h-5 text-blue-600" /> Edit Customer
          </DialogTitle>
          <form onSubmit={handleSaveEdit} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="edit-name">Full Name <span className="text-red-500">*</span></Label>
                <Input
                  id="edit-name"
                  value={editForm.name}
                  onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                  required
                  data-testid="edit-customer-name"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="edit-email">Email Address <span className="text-red-500">*</span></Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editForm.email}
                  onChange={e => setEditForm({ ...editForm, email: e.target.value })}
                  required
                  data-testid="edit-customer-email"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  value={editForm.phone}
                  onChange={e => setEditForm({ ...editForm, phone: e.target.value })}
                  placeholder="(555) 000-0000"
                  data-testid="edit-customer-phone"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="edit-address">Street Address</Label>
                <Input
                  id="edit-address"
                  value={editForm.address}
                  onChange={e => setEditForm({ ...editForm, address: e.target.value })}
                  data-testid="edit-customer-address"
                />
              </div>
              <div>
                <Label htmlFor="edit-city">City</Label>
                <Input
                  id="edit-city"
                  value={editForm.city}
                  onChange={e => setEditForm({ ...editForm, city: e.target.value })}
                  data-testid="edit-customer-city"
                />
              </div>
              <div>
                <Label htmlFor="edit-state">State</Label>
                <Input
                  id="edit-state"
                  value={editForm.state}
                  onChange={e => setEditForm({ ...editForm, state: e.target.value })}
                  data-testid="edit-customer-state"
                />
              </div>
              <div>
                <Label htmlFor="edit-zip">ZIP Code</Label>
                <Input
                  id="edit-zip"
                  value={editForm.zip_code}
                  onChange={e => setEditForm({ ...editForm, zip_code: e.target.value })}
                  data-testid="edit-customer-zip"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={savingEdit} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="edit-customer-save">
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Customer Confirmation */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="delete-customer-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteCustomer?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes {deleteCustomer?.email}'s customer profile and login account.
              Their past orders/licenses are kept on record. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-customer-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCustomer}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="delete-customer-confirm"
            >
              {deleting ? 'Deleting...' : 'Delete Customer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminCustomers;
